/**
 * Conversion-Auswertung der Kontaktanfragen.
 *
 * Beantwortet zwei Fragen: "Von den letzten 10 Anfragen sind wie viele Kunden
 * geworden?" und "Wie viele waren es im Zeitraum X?"
 *
 * Zwei Dinge machen die naive Rechnung (Treffer / Anfragen) irrefuehrend:
 *
 * 1. REIFEZEIT. Zwischen Anfrage und Anmeldung liegen erfahrungsgemaess Wochen.
 *    Eine Anfrage von letzter Woche ist deshalb nicht "nicht konvertiert",
 *    sondern noch offen. Die Schwelle dafuer wird aus den tatsaechlich
 *    beobachteten Abstaenden abgeleitet, nicht geraten.
 *
 * 2. BESTANDSMITGLIEDER. Schreibt jemand an, der laengst Mitglied ist, ist das
 *    keine Conversion. Solche Faelle fallen aus der Grundgesamtheit heraus,
 *    ebenso Anfragen ohne E-Mail - ohne Adresse gibt es keinen Abgleich, und
 *    "nicht konvertiert" waere dort eine Behauptung ohne Grundlage.
 */

import React, { useState, useMemo } from 'react';
import { ChartCard, SegmentedControl, ColumnChart, ShareBar, StatTile, StatGrid, LIFECYCLE_COLORS, SERIES_COLORS } from './charts/Charts';
import './LeadsConversion.css';

const TAG = 86400000;
const MONATE = ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const FARBEN = {
  konvertiert: LIFECYCLE_COLORS.active,   // aqua – wie "Aktiv" bei den Mitgliedern
  // Orange statt Violett: Violett neben dem Blau von "noch offen" ist unter
  // Rot-Gruen-Schwaeche schwer zu trennen. Aqua-Orange-Blau sind in der
  // Referenzpalette benachbarte, gepruefte Slots.
  pt: SERIES_COLORS[1],                   // orange – gewonnen, aber anderes Produkt
  offen: LIFECYCLE_COLORS.paused,         // blau – Ausgang noch unentschieden
  nicht: '#94a3b8',                       // grau – kein Zustand, bewusst zurueckhaltend
};

/** Personal Training verkauft statt Mitgliedschaft - ebenfalls ein Abschluss. */
const istPT = (status) => String(status).startsWith('pt_');

const ZEITRAEUME = [
  { value: 3, label: '3 Monate' },
  { value: 12, label: '12 Monate' },
  { value: 0, label: 'Alles' },
];

/** Fallback, solange es zu wenige Anmeldungen gibt, um daraus zu lernen. */
const REIFEZEIT_FALLBACK = 90;

const monatsLabel = (d) => `${MONATE[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
const monatsKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const LeadsConversion = ({ leads }) => {
  const [zeitraum, setZeitraum] = useState(3);

  const auswertung = useMemo(() => {
    const jetzt = Date.now();

    // Jede Anfrage einordnen
    const bewertet = leads.map((l) => {
      const anfrage = new Date(l.created_at);
      const m = l.mitglied;
      const beitritt = m && m.member_since ? new Date(m.member_since) : null;

      // Toleranz von einem Tag: Anfrage und Anmeldung am selben Tag zaehlen als Conversion
      const bestandsmitglied = beitritt && beitritt.getTime() < anfrage.getTime() - TAG;
      if (bestandsmitglied) return { lead: l, anfrage, klasse: 'bestand', beitritt };

      // Der Mitglieder-Abgleich ist die harte Evidenz, der Status die manuelle
      // Einschaetzung. Eine der beiden reicht - so zaehlt eine Conversion auch,
      // wenn jemand ohne E-Mail angefragt und danach unterschrieben hat.
      const tage = beitritt ? Math.round((beitritt - anfrage) / TAG) : null;
      if (m) return { lead: l, anfrage, klasse: 'konvertiert', beitritt, tage };
      if (l.status === 'converted') return { lead: l, anfrage, klasse: 'konvertiert', tage: null };
      // PT zaehlt als gewonnen, auch ohne E-Mail-Treffer - der Status ist die Evidenz
      if (istPT(l.status)) return { lead: l, anfrage, klasse: 'pt' };
      if (l.status === 'lost' || l.status === 'closed') return { lead: l, anfrage, klasse: 'entschieden-nein' };

      // Ohne E-Mail und ohne gesetzten Status gibt es keine Grundlage fuer ein Urteil
      if (!l.email) return { lead: l, anfrage, klasse: 'ohne-email' };
      return { lead: l, anfrage, klasse: 'offen-oder-nicht' };
    });

    const konvertiert = bewertet.filter((b) => b.klasse === 'konvertiert');
    const abstaende = konvertiert.map((b) => b.tage).filter((t) => t != null).sort((a, b) => a - b);

    // Reifezeit aus den beobachteten Abstaenden: der langsamste bisherige Fall.
    // Solange zu wenige Anmeldungen vorliegen, bleibt es beim Erfahrungswert.
    const reifezeit = abstaende.length >= 3 ? Math.max(30, abstaende[abstaende.length - 1]) : REIFEZEIT_FALLBACK;
    const median = abstaende.length ? abstaende[Math.floor(abstaende.length / 2)] : null;

    // Endgueltige Klasse: was noch reifen kann, ist nicht gescheitert
    const basis = bewertet
      .filter((b) => b.klasse !== 'ohne-email' && b.klasse !== 'bestand')
      .map((b) => {
        if (b.klasse === 'konvertiert') return { ...b, endklasse: 'konvertiert' };
        if (b.klasse === 'pt') return { ...b, endklasse: 'pt' };
        // Als verloren markiert ist eine Entscheidung, da reift nichts mehr nach
        if (b.klasse === 'entschieden-nein') return { ...b, endklasse: 'nicht' };
        const alterTage = (jetzt - b.anfrage.getTime()) / TAG;
        return { ...b, endklasse: alterTage <= reifezeit ? 'offen' : 'nicht' };
      })
      .sort((a, b) => b.anfrage - a.anfrage);

    return {
      basis,
      reifezeit,
      median,
      ohneEmail: bewertet.filter((b) => b.klasse === 'ohne-email').length,
      bestand: bewertet.filter((b) => b.klasse === 'bestand').length,
    };
  }, [leads]);

  const { basis, reifezeit, median, ohneEmail, bestand } = auswertung;

  // --- "Von den letzten 10 Anfragen ..." ---
  const gewonnen = (b) => b.endklasse === 'konvertiert' || b.endklasse === 'pt';

  const letzte10 = basis.slice(0, 10);
  const letzte10Konvertiert = letzte10.filter(gewonnen).length;
  const letzte10PT = letzte10.filter((b) => b.endklasse === 'pt').length;
  const letzte10Offen = letzte10.filter((b) => b.endklasse === 'offen').length;

  // --- "Im Zeitraum ..." ---
  const grenze = zeitraum ? Date.now() - zeitraum * 30.44 * TAG : 0;
  const imZeitraum = basis.filter((b) => b.anfrage.getTime() >= grenze);
  const zKonvertiert = imZeitraum.filter(gewonnen).length;
  const zPT = imZeitraum.filter((b) => b.endklasse === 'pt').length;
  const zOffen = imZeitraum.filter((b) => b.endklasse === 'offen').length;
  const zQuote = imZeitraum.length ? Math.round((zKonvertiert / imZeitraum.length) * 100) : 0;
  const zeitraumLabel = zeitraum ? `Letzte ${ZEITRAEUME.find((z) => z.value === zeitraum).label}` : 'Gesamter Zeitraum';

  // --- Monatskohorten fuer das Diagramm ---
  const monate = useMemo(() => {
    const eimer = new Map();
    imZeitraum.forEach((b) => {
      const key = monatsKey(b.anfrage);
      if (!eimer.has(key)) {
        eimer.set(key, { key, label: monatsLabel(b.anfrage), konvertiert: 0, pt: 0, offen: 0, nicht: 0 });
      }
      eimer.get(key)[b.endklasse]++;
    });
    return [...eimer.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [imZeitraum]);

  if (leads.length === 0) return null;

  return (
    <section className="leads-conversion">
      <div className="leads-conversion-head">
        <div>
          <h3>Aus Anfragen werden Kunden</h3>
          <p className="leads-conversion-sub">
            Abgeglichen über die E-Mail-Adresse der Anfrage
          </p>
        </div>
        <SegmentedControl options={ZEITRAEUME} value={zeitraum} onChange={setZeitraum} ariaLabel="Zeitraum" />
      </div>

      <StatGrid>
        <StatTile
          hero
          label="Letzte 10 Anfragen"
          value={`${letzte10Konvertiert} von ${letzte10.length}`}
          sub={[
            letzte10PT > 0 ? `${letzte10PT} davon über PT` : null,
            letzte10Offen > 0 ? `${letzte10Offen} noch zu jung für ein Urteil` : null,
          ].filter(Boolean).join(' · ') || 'alle bereits entschieden'}
        >
          {/* Der Streifen zeigt sofort, wie viel der Zahl noch offen ist */}
          <ShareBar
            compact
            segments={[
              { label: 'Mitglied geworden', value: letzte10Konvertiert - letzte10PT, color: FARBEN.konvertiert },
              { label: 'PT verkauft', value: letzte10PT, color: FARBEN.pt },
              { label: 'Noch offen', value: letzte10Offen, color: FARBEN.offen },
              { label: 'Ohne Abschluss', value: letzte10.length - letzte10Konvertiert - letzte10Offen, color: FARBEN.nicht },
            ]}
          />
        </StatTile>
        <StatTile
          label={zeitraumLabel}
          value={`${zKonvertiert} von ${imZeitraum.length}`}
          sub={[
            `${zQuote} % Conversion`,
            zPT > 0 ? `${zPT} über PT` : null,
            zOffen > 0 ? `${zOffen} noch offen` : null,
          ].filter(Boolean).join(' · ')}
        />
        <StatTile
          label="Bis zur Anmeldung"
          value={median != null ? `${median} Tage` : '–'}
          sub={median != null ? `im Median · längstens bisher ${reifezeit} Tage` : 'noch keine Anmeldung erfasst'}
        />
      </StatGrid>

      <ChartCard
        title="Anfragen nach Monat"
        subtitle="Jede Anfrage in dem Monat, in dem sie eingegangen ist"
        table={{
          columns: ['Monat', 'Anfragen', 'Mitglied geworden', 'PT verkauft', 'Noch offen', 'Ohne Abschluss'],
          rows: monate.map((m) => [
            m.label,
            m.konvertiert + m.pt + m.offen + m.nicht,
            m.konvertiert,
            m.pt,
            m.offen,
            m.nicht,
          ]),
        }}
        footnote={[
          `„Noch offen" heißt: die Anfrage ist jünger als ${reifezeit} Tage – so lange hat es bisher längstens bis zur Anmeldung gedauert.`,
          ohneEmail > 0 ? `${ohneEmail} Anfrage${ohneEmail === 1 ? '' : 'n'} ohne E-Mail-Adresse ${ohneEmail === 1 ? 'ist' : 'sind'} nicht abgleichbar und bleib${ohneEmail === 1 ? 't' : 'en'} außen vor.` : null,
          bestand > 0 ? `${bestand} Anfrage${bestand === 1 ? '' : 'n'} ${bestand === 1 ? 'kam' : 'kamen'} von jemandem, der zu dem Zeitpunkt schon Mitglied war – das ist keine Conversion.` : null,
        ].filter(Boolean).join(' ')}
      >
        <ColumnChart
          categories={monate.map((m) => m.label)}
          series={[
            { id: 'konvertiert', name: 'Mitglied geworden', color: FARBEN.konvertiert, values: monate.map((m) => m.konvertiert) },
            { id: 'pt', name: 'PT verkauft', color: FARBEN.pt, values: monate.map((m) => m.pt) },
            { id: 'offen', name: 'Noch offen', color: FARBEN.offen, values: monate.map((m) => m.offen) },
            { id: 'nicht', name: 'Ohne Abschluss', color: FARBEN.nicht, values: monate.map((m) => m.nicht) },
          ]}
          stacked
          height={230}
          ariaLabel="Anfragen je Monat, aufgeteilt nach Ausgang"
        />
      </ChartCard>
    </section>
  );
};

export default LeadsConversion;
